import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { tokenRefreshService } from '../services/tokenRefresh';
import { getProfile } from '../api/auth.service';
import { queryClient } from '@/lib/query-client';
import { prefetchAvatar } from '@/features/files';
import type { LoginResponse, ProfileResponse } from '../types/schemas';

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: ProfileResponse['response_output']['detail'] | null;
  setAuth: (data: LoginResponse) => void;
  clearAuth: () => void;
  isAuthenticated: () => boolean;
  getStoredToken: () => string | null;
  getStoredUser: () => unknown | null;
  getRefreshToken: () => string | null;
  updateAccessToken: (token: string) => void;
  refreshTokenIfNeeded: () => Promise<string | null>;
  fetchUser: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,

      setAuth: (data: LoginResponse) => {
        const refreshToken = data.response_output.detail.refresh_token || null;
        set({
          accessToken: data.response_output.detail.access_token,
          refreshToken,
          user: null,
        });
      },

      clearAuth: () => {
        set({
          accessToken: null,
          refreshToken: null,
          user: null,
        });
        
        queryClient.clear();

        tokenRefreshService.onRefreshFailed();
      },

      isAuthenticated: () => {
        return Boolean(get().accessToken);
      },

      getStoredToken: () => {
        return get().accessToken;
      },

      getStoredUser: () => {
        return get().user;
      },

      getRefreshToken: () => {
        return get().refreshToken;
      },

      updateAccessToken: (token: string) => {
        set({ accessToken: token });
      },

      refreshTokenIfNeeded: async () => {
        const { refreshToken, updateAccessToken } = get();

        if (!refreshToken) {
          get().clearAuth();
          return null;
        }

        try {
          const newAccessToken = await tokenRefreshService.refreshAccessToken(refreshToken);
          if (newAccessToken) {
            updateAccessToken(newAccessToken);
            await get().fetchUser();
            return newAccessToken;
          } else {
            get().clearAuth();
            return null;
          }
        } catch {
          get().clearAuth();
          return null;
        }
      },

      fetchUser: async () => {
        try {
          const response = await getProfile();
          set({ user: response.response_output.detail });

          // Prefetch avatar to warm up cache
          const user = response.response_output.detail;
          if (user?.avatar_file_id) {
            prefetchAvatar(queryClient, user.avatar_file_id);
          }
        } catch (error) {
          console.error('Failed to fetch user profile:', error);
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
      }),
    }
  )
);


